import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, STATUS_META, PRIORITY_META } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, Image as ImageIcon, FileText, Sheet, Video, File as FileIcon, Trash2, X } from "lucide-react";

const kindIcon = { image: ImageIcon, document: FileText, spreadsheet: Sheet, video: Video, other: FileIcon };

const emptyForm = {
  title: "", description: "", status: "open", priority: "medium",
  due_date: "", property_location: "", assignee_id: "unassigned", assignee_name: "",
};

export default function IssueModal({ open, onClose, issue, initialDraft, team, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [current, setCurrent] = useState(issue);
  const fileRef = useRef();

  useEffect(() => {
    if (issue) {
      setForm({
        title: issue.title || "", description: issue.description || "",
        status: issue.status || "open", priority: issue.priority || "medium",
        due_date: issue.due_date || "", property_location: issue.property_location || "",
        assignee_id: issue.assignee_id || "unassigned", assignee_name: issue.assignee_name || "",
      });
      setCurrent(issue);
    } else if (initialDraft) {
      setForm({
        ...emptyForm,
        title: initialDraft.title || "",
        description: initialDraft.description || "",
        priority: initialDraft.priority || "medium",
        due_date: initialDraft.due_date || "",
        property_location: initialDraft.property_location || "",
      });
      setCurrent(null);
    } else {
      setForm(emptyForm);
      setCurrent(null);
    }
  }, [issue, initialDraft, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleAssignee = (uid) => {
    if (uid === "unassigned") {
      set("assignee_id", "unassigned");
      set("assignee_name", "");
    } else {
      const m = team.find((t) => t.user_id === uid);
      setForm((f) => ({ ...f, assignee_id: uid, assignee_name: m ? m.name : "" }));
    }
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        assignee_id: form.assignee_id === "unassigned" ? null : form.assignee_id,
        due_date: form.due_date || null,
      };
      let saved;
      if (current) {
        const res = await api.put(`/issues/${current.id}`, payload);
        saved = res.data;
      } else {
        const res = await api.post("/issues", payload);
        saved = res.data;
      }
      setCurrent(saved);
      toast.success(current ? "Issue updated" : "Issue created");
      onSaved();
      if (!current) {
        // keep modal open so user can attach files to the new issue
      }
    } catch (e) {
      toast.error("Failed to save issue");
    } finally {
      setSaving(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!current) {
      toast.error("Save the issue first, then add files");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post(`/issues/${current.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCurrent(res.data);
      toast.success("File uploaded");
      onSaved();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAttachment = async (attId) => {
    try {
      const res = await api.delete(`/issues/${current.id}/attachments/${attId}`);
      setCurrent(res.data);
      onSaved();
    } catch (e) {
      toast.error("Failed to remove file");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="issue-modal">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl">{current ? "Edit Issue" : "New Maintenance Issue"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-zinc-500">Title</Label>
            <Input
              data-testid="issue-title-input"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. Leaking faucet in Unit 4B"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-zinc-500">Description</Label>
            <Textarea
              data-testid="issue-description-input"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Describe the maintenance issue…"
              className="mt-1.5 min-h-[90px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-zinc-500">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="mt-1.5" data-testid="issue-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-zinc-500">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger className="mt-1.5" data-testid="issue-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-zinc-500">Due Date</Label>
              <Input
                type="date"
                data-testid="issue-duedate-input"
                value={form.due_date}
                onChange={(e) => set("due_date", e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-zinc-500">Property / Unit</Label>
              <Input
                data-testid="issue-property-input"
                value={form.property_location}
                onChange={(e) => set("property_location", e.target.value)}
                placeholder="e.g. 12 Oak St, Unit 4B"
                className="mt-1.5"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-zinc-500">Assign To</Label>
            <Select value={form.assignee_id} onValueChange={handleAssignee}>
              <SelectTrigger className="mt-1.5" data-testid="issue-assignee-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {team.map((m) => (
                  <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Attachments */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider font-bold text-zinc-500">Attachments</Label>
              <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="file-input" />
              <Button
                type="button" variant="outline" size="sm"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                data-testid="upload-file-button"
                className="gap-2"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Add file
              </Button>
            </div>
            {!current && (
              <p className="text-xs text-zinc-400 mt-2">Save the issue first to attach photos, documents, spreadsheets or videos.</p>
            )}
            <div className="mt-3 space-y-2">
              {current?.attachments?.map((a) => {
                const Icon = kindIcon[a.kind] || FileIcon;
                return (
                  <div key={a.id} className="flex items-center gap-3 border border-zinc-200 rounded-md px-3 py-2 bg-zinc-50" data-testid="attachment-item">
                    <Icon className="h-4 w-4 text-zinc-500 shrink-0" />
                    <a
                      href={`${api.defaults.baseURL}/files/${a.storage_path}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-sm text-zinc-800 hover:underline truncate flex-1"
                    >
                      {a.original_filename}
                    </a>
                    <span className="text-xs text-zinc-400">{(a.size / 1024).toFixed(0)} KB</span>
                    <button onClick={() => removeAttachment(a.id)} data-testid="remove-attachment-button" className="text-zinc-400 hover:text-red-600 transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="cancel-issue-button"><X className="h-4 w-4 mr-1" />Close</Button>
          <Button onClick={save} disabled={saving} data-testid="save-issue-button" className="bg-zinc-900 hover:bg-zinc-800">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {current ? "Save changes" : "Create issue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
